import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { MapPin, Calendar, FileText, Phone, Mail, ExternalLink, Layers } from 'lucide-react';
import { getJobDetail } from '../api/client.js';
import type { JobFieldEstimateRow } from '@gutterinv/shared';
import { Badge } from './ui/badge.js';
import { Skeleton } from './ui/skeleton.js';
import {
  SheetBody,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from './ui/sheet.js';
import { formatCents, formatDate, formatMarginPct } from '../lib/format.js';
import { JobStatusBadge } from './JobStatusBadge.js';
import { InlineJobNameEditor } from './InlineJobNameEditor.js';
import { productLineLabel } from '../lib/time-entry-format.js';

// Cost / Sell / Margin column geometry for the profitability roll-up. Fixed-width
// columns (not auto) so every row's COST / SELL / MARGIN right-aligns on the same x
// across the separate per-row grids — mirrors PricingCalculator's READOUT_GRID so the
// quickview ties out visually to the View Profitability modal.
const PROFIT_GRID = 'grid grid-cols-[minmax(0,1fr)_5.5rem_7rem_4rem] gap-x-3';

// READ-ONLY snapshot of a job. The side card is a quick glance only — it
// has NO actions (no schedule, no edit-estimate, no delete, no status
// changes). Every mutation lives on the full job page, reached via the
// expand button (top-right).
export function JobDetailPanel({
  jobId,
  onClose,
}: {
  jobId: string;
  /** Kept for caller compatibility; the drawer no longer deletes. */
  onDeleted?: () => void;
  onClose?: () => void;
}) {
  const navigate = useNavigate();

  const { data: job, isLoading, error } = useQuery({
    queryKey: ['job', jobId],
    queryFn: () => getJobDetail(jobId),
  });

  if (isLoading) {
    return (
      <>
        <SheetHeader>
          <Skeleton className="h-6 w-32" />
          <Skeleton className="h-7 w-64" />
        </SheetHeader>
        <SheetBody className="space-y-4">
          <Skeleton className="h-24" />
          <Skeleton className="h-32" />
          <Skeleton className="h-48" />
        </SheetBody>
      </>
    );
  }

  if (!job) {
    return (
      <>
        <SheetHeader>
          <SheetTitle>Failed to load job</SheetTitle>
        </SheetHeader>
        <SheetBody>
          <div className="text-sm text-destructive">
            {error instanceof Error ? error.message : 'Unknown error'}
          </div>
        </SheetBody>
      </>
    );
  }

  const hasQuote = job.quoteAmountCents !== null;

  return (
    <>
      <button
        onClick={() => {
          onClose?.();
          navigate(`/jobs/${job.id}`);
        }}
        className="absolute right-12 top-4 rounded-sm opacity-70 transition-opacity hover:opacity-100"
        title="Open full page"
      >
        <ExternalLink className="h-4 w-4" />
      </button>

      <SheetHeader>
        <div className="flex items-center gap-2">
          <JobStatusBadge status={job.status} />
          {job.poNumber && (
            <Badge variant="outline" className="font-mono">
              {job.poNumber}
            </Badge>
          )}
        </div>
        <SheetTitle>
          <InlineJobNameEditor jobId={job.id} value={job.displayName} variant="title" />
        </SheetTitle>
        <SheetDescription>Created {formatDate(job.createdAt)}</SheetDescription>
      </SheetHeader>

      <SheetBody className="space-y-6">
        {/* Customer LEFT, Site RIGHT — two columns, matching the main Job card's
            Customer & Site layout. Stacks on a sub-640px viewport. */}
        <section>
          <h3 className="eyebrow mb-2">Customer &amp; Site</h3>
          <div className="grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
            {/* Customer */}
            <div className="space-y-1.5">
              <div className="font-semibold text-foreground">{job.customer.name}</div>
              <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                <span className="capitalize">{job.customer.type}</span>
                {job.customer.discountPct > 0 && (
                  <span className="tnum">{job.customer.discountPct}% discount</span>
                )}
              </div>
              {job.customer.phone && (
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Phone className="h-3 w-3" />
                  {job.customer.phone}
                </div>
              )}
              {job.customer.email && (
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Mail className="h-3 w-3" />
                  {job.customer.email}
                </div>
              )}
            </div>
            {/* Site */}
            <div className="space-y-1.5">
              <div className="flex items-start gap-1.5">
                <MapPin className="mt-0.5 h-3 w-3 shrink-0 text-taupe" />
                <span className="text-foreground">{job.address}</span>
              </div>
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Layers className="h-3 w-3 text-taupe" />
                <span>{productLineLabel(job.productLine)}</span>
              </div>
              {job.scheduledStartDate && (
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <Calendar className="h-3 w-3 text-taupe" />
                  <span>Install {formatDate(job.scheduledStartDate)}</span>
                </div>
              )}
            </div>
          </div>
        </section>

        {hasQuote && (
          <section>
            <h3 className="eyebrow mb-2 flex items-center gap-1.5">
              <FileText className="h-3 w-3" />
              Quote
            </h3>
            <div className="rounded-md border border-border bg-secondary/30 p-3">
              <div className="tnum text-2xl font-bold text-foreground">
                {formatCents(job.quoteTotalCents)}
              </div>
              {job.quoteSentAt && (
                <div className="mt-1 text-xs text-muted-foreground">
                  Sent {formatDate(job.quoteSentAt)}
                </div>
              )}
            </div>
          </section>
        )}

        {job.fieldEstimates.length > 0 && (
          <section>
            <h3 className="eyebrow mb-2">Line items ({job.fieldEstimates.length})</h3>
            <div className="divide-y divide-border rounded-md border border-border">
              {job.fieldEstimates.map((e) => (
                <FieldEstimateRow key={e.id} estimate={e} />
              ))}
            </div>
            {/* Profitability roll-up — Materials / Labor / Job total with Cost, Sell,
                Margin, mirroring the View Profitability modal (same formatMarginPct +
                column geometry). Labor is totals-only (consume the pre-computed
                quoteLaborChargeCents / quoteLaborCostCents — frozen when quoted, live
                otherwise; no footage math here). Cost + Margin columns are owner-gated
                via estimatedCostCents (null when the user can't see cost), exactly like
                the old "cost basis (owner)" line — so labor cost can't leak either. */}
            {(() => {
              const matSell = job.estimatedPriceCents;
              const costVisible = job.estimatedCostCents !== null;
              const matCost = job.estimatedCostCents ?? 0;
              const laborCharge = job.quoteLaborChargeCents;
              const laborCost = job.quoteLaborCostCents;
              const jobSell = job.quoteTotalCents; // = materials + misc + labor (tax-free; misc is discount-exempt)
              const jobCost = matCost + laborCost; // cost never folded into jobSell
              const hasLabor = laborCharge > 0 || laborCost > 0;
              const margin = (cost: number, sell: number) =>
                cost > 0 ? formatMarginPct(cost, sell) : '—';
              return (
                <div className="mt-3 space-y-2 rounded-md border border-border bg-secondary/30 px-3 py-3">
                  <div className="text-[0.6875rem] uppercase tracking-wide text-taupe">
                    Profitability
                  </div>
                  {costVisible ? (
                    <>
                      <div className={`${PROFIT_GRID} text-[0.625rem] uppercase tracking-wider text-taupe`}>
                        <span />
                        <span className="text-right">Cost</span>
                        <span className="text-right">Sell</span>
                        <span className="text-right">Margin</span>
                      </div>
                      <div className={`${PROFIT_GRID} text-sm text-muted-foreground`}>
                        <span>Materials</span>
                        <span className="tnum text-right">{formatCents(matCost)}</span>
                        <span className="tnum text-right">{formatCents(matSell)}</span>
                        <span className="tnum text-right">{margin(matCost, matSell)}</span>
                      </div>
                      {job.miscLines.length > 0 && (
                        <div className={`${PROFIT_GRID} text-sm text-muted-foreground`}>
                          <span>Miscellaneous</span>
                          <span className="tnum text-right">{formatCents(0)}</span>
                          <span className="tnum text-right">{formatCents(job.miscTotalCents)}</span>
                          <span className="tnum text-right">100%</span>
                        </div>
                      )}
                      {hasLabor && (
                        <div className={`${PROFIT_GRID} text-sm text-muted-foreground`}>
                          <span>Labor</span>
                          <span className="tnum text-right">{formatCents(laborCost)}</span>
                          <span className="tnum text-right">{formatCents(laborCharge)}</span>
                          <span className="tnum text-right">{margin(laborCost, laborCharge)}</span>
                        </div>
                      )}
                      <div className="border-t border-border pt-2">
                        <div className={`${PROFIT_GRID} items-baseline text-sm font-semibold text-foreground`}>
                          <span className="text-base">Job total</span>
                          <span className="tnum text-right">{formatCents(jobCost)}</span>
                          <span className="tnum text-right text-base">{formatCents(jobSell)}</span>
                          <span className="tnum text-right">{margin(jobCost, jobSell)}</span>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* No cost visibility for this user → sell-only roll-up (cost +
                          margin hidden, never '—' faked). */}
                      <div className="flex items-center justify-between text-sm text-muted-foreground">
                        <span>Materials</span>
                        <span className="tnum">{formatCents(matSell)}</span>
                      </div>
                      {job.miscLines.length > 0 && (
                        <div className="flex items-center justify-between text-sm text-muted-foreground">
                          <span>Miscellaneous</span>
                          <span className="tnum">{formatCents(job.miscTotalCents)}</span>
                        </div>
                      )}
                      {hasLabor && (
                        <div className="flex items-center justify-between text-sm text-muted-foreground">
                          <span>Labor</span>
                          <span className="tnum">{formatCents(laborCharge)}</span>
                        </div>
                      )}
                      <div className="flex items-center justify-between border-t border-border pt-2 text-sm font-semibold text-foreground">
                        <span className="text-base">Job total</span>
                        <span className="tnum text-base">{formatCents(jobSell)}</span>
                      </div>
                    </>
                  )}
                </div>
              );
            })()}
          </section>
        )}
      </SheetBody>
    </>
  );
}

function FieldEstimateRow({ estimate }: { estimate: JobFieldEstimateRow }) {
  return (
    <div className="flex items-start justify-between gap-3 px-3 py-2.5">
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-foreground">
          {estimate.skuName ?? estimate.freehandText ?? '(unknown)'}
          {estimate.variant && (
            <span className="font-normal text-muted-foreground"> · {estimate.variant}</span>
          )}
        </div>
        <div className="mt-0.5 flex items-center gap-1.5 text-xs text-taupe">
          {estimate.skuCode && <span className="font-mono">{estimate.skuCode}</span>}
          <span>·</span>
          <span className="capitalize">{estimate.status}</span>
        </div>
      </div>
      <div className="text-right">
        <div className="tnum text-sm font-semibold text-foreground">
          {estimate.quantity.toLocaleString()}
          {estimate.unit && <span className="ml-1 text-xs text-taupe">{estimate.unit}</span>}
        </div>
        <div className="tnum text-xs text-muted-foreground">
          {formatCents(estimate.priceCentsAtEstimate * estimate.quantity)}
        </div>
      </div>
    </div>
  );
}
